import ScheduleCreateModal from '@/components/Schedules/Kanban/ScheduleCreateModal';
import ScheduleEditModal from '@/components/Schedules/Kanban/ScheduleEditModal';
import {
  CompaniesInfiniteSelectField,
  DatePicker,
  DefaultRouteTabs,
  EditIcon,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import AgendaList, { AgendaData } from '@/components/_common/AgendaList';
import WorkOrderStatusTag from '@/components/_common/WorkOrderStatusTag';
import { useActivityCalendar } from '@/core/domain/visual-management/activities/activities.hooks';
import { GPActivityCalendar } from '@/core/domain/visual-management/activities/activities.types';
import { useGPStatusList } from '@/core/domain/visual-management/status/status.hooks';
import { useDisclosure, useWindowSize } from '@/hooks';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import {
  Alert,
  Button,
  Calendar,
  Col,
  Form,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
} from 'antd';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export enum AccompanimentTypeEnum {
  calendar = 'calendar',
  list = 'list',
}

const filterKey: Record<number, keyof GPActivityCalendar> = {
  0: 'createdDate',
  1: 'entryDate',
  2: 'estimatedLeaveDate',
};

export default function ScheduleAccompanimentPage({ route }: PageProps) {
  const intl = useIntl();

  const [form] = Form.useForm<{ companyIds: number[]; paginateBy: number }>();
  const companyIds = Form.useWatch('companyIds', form);
  const paginateBy = Form.useWatch('paginateBy', form);
  const statusIds = Form.useWatch('statusIds', form);

  const { isMobile } = useWindowSize();
  const [date, setDate] = useState<Moment>(moment());

  const [activityId, setActivityId] = useState<string>();
  const [accompanimentType, setAccompanimentType] =
    useState<AccompanimentTypeEnum>(AccompanimentTypeEnum.calendar);

  const [scheduleAddOpened, scheduleAddAction] = useDisclosure();
  const [modalEditOpened, modalEditAction] = useDisclosure();

  useEffect(() => {
    const storageCompanyIds = localStorage.getItem('uc-schedule-companyIds');

    if (storageCompanyIds?.length) {
      form.setFieldValue(
        'companyIds',
        storageCompanyIds.split(',').map(Number),
      );
    }
  }, []);

  const isCalendar = accompanimentType === AccompanimentTypeEnum.calendar;

  const {
    data: schedules,
    dataUpdatedAt,
    isFetching,
  } = useActivityCalendar({
    companyIds,
    month: date.month() + 1,
    year: date.year(),
    paginateBy: paginateBy || 0,
  });

  const statusList = useGPStatusList(!isCalendar);

  const handleSelectSchedule = (id: string) => {
    if (id) {
      setActivityId(id);

      modalEditAction.open();
    }
  };

  const dateCellRender = useCallback(
    (value: Moment) => {
      if (isFetching) {
        return (
          <Space wrap>
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
          </Space>
        );
      }

      if (value.isBefore(date, 'month') || value.isAfter(date, 'month')) {
        return null;
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            width: '100%',
            gap: 4,
          }}
        >
          {!!schedules &&
            schedules
              ?.filter((item) =>
                value.isSame(
                  moment(item[filterKey[paginateBy]] as string),
                  'day',
                ),
              )
              .map((item) => (
                <WorkOrderStatusTag
                  key={item.activityId}
                  color={item.status?.color || 'gray'}
                  label={
                    item.licensePlate
                      ? `${item.licensePlate} • ${item.brand}`
                      : item.customer
                  }
                  style={{ width: '100%' }}
                  onClick={() => handleSelectSchedule(item.activityId)}
                />
              ))}
        </div>
      );
    },
    [dataUpdatedAt],
  );

  function agendaTag(item: GPActivityCalendar) {
    return (
      <WorkOrderStatusTag
        key={item.activityId}
        color={item.status?.color || 'gray'}
        label={
          item.licensePlate
            ? `${item.licensePlate} • ${item.brand}`
            : item.customer
        }
        style={{ width: '100%' }}
        onClick={() => handleSelectSchedule(item.activityId)}
      />
    );
  }

  const getAgendaData = useCallback(() => {
    return (
      schedules?.reduce<AgendaData[]>((acc, item) => {
        const isSameDay = (date: Moment) =>
          moment(item[filterKey[paginateBy]] as string).isSame(date, 'day');

        if (acc.find((accItem) => isSameDay(accItem.date))) {
          return acc.map((accItem) => {
            if (isSameDay(accItem.date)) {
              return {
                ...accItem,
                render: [...accItem.render, agendaTag(item)],
              };
            }
            return accItem;
          });
        }

        return [
          ...acc,
          { date: moment(item.entryDate), render: [agendaTag(item)] },
        ];
      }, [] as AgendaData[]) || []
    );
  }, [schedules]);

  const filteredSchedules = !!statusIds?.length
    ? schedules?.filter((schedule) => statusIds?.includes(schedule.status?.id))
    : schedules;

  return (
    <DefaultRouteTabs
      route={route.parent}
      permissions={route.permissions}
      extra={
        <Button
          block
          type="primary"
          disabled={
            !hasPermissions([Authorities.SHEDULE_EVERYTHING_VIEW]) ||
            !companyIds?.length
          }
          onClick={scheduleAddAction.open}
        >
          Criar agendamento
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form}>
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label="Selecione uma empresa"
              name="companyIds"
              getValueFromEvent={(id) => {
                localStorage.setItem('uc-schedule-companyIds', String(id));

                return id;
              }}
            >
              <CompaniesInfiniteSelectField
                mode="multiple"
                queryEnable
                queryFilter={{ rows: 50 }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label="Filtrar por"
              name="paginateBy"
              initialValue={2}
            >
              <Select
                options={[
                  { value: 0, label: 'Data de criação' },
                  { value: 1, label: 'Data de entrada' },
                  { value: 2, label: 'Data de entrega' },
                ]}
              />
            </FloatingFormItem>
          </Col>

          {!isCalendar && (
            <>
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem label="Data" name="date" initialValue={date}>
                  <DatePicker onChange={(value) => setDate(moment(value))} />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem name="statusIds" label="Status">
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                    allowClear
                    options={statusList.data?.map((status) => ({
                      value: status.id,
                      label: (
                        <Group gap={8}>
                          <div
                            style={{
                              width: 15,
                              height: 15,
                              borderRadius: 4,
                              backgroundColor: status.color,
                            }}
                          />
                          <span style={{ fontWeight: 500 }}>{status.name}</span>
                        </Group>
                      ),
                    }))}
                  />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Form>

      <Radio.Group
        options={[
          { label: 'Calendário', value: AccompanimentTypeEnum.calendar },
          { label: 'Listagem', value: AccompanimentTypeEnum.list },
        ]}
        value={accompanimentType}
        onChange={({ target: { value } }) => setAccompanimentType(value)}
        defaultValue={AccompanimentTypeEnum.calendar}
        buttonStyle="solid"
        optionType="button"
        style={{ marginBottom: 24 }}
      />

      {isMobile && isCalendar && (
        <AgendaList
          dataSource={getAgendaData()}
          onChange={setDate}
          date={date}
        />
      )}

      {!isMobile && isCalendar && (
        <Calendar
          key={dataUpdatedAt}
          value={date}
          dateCellRender={dateCellRender}
          mode="month"
          onChange={setDate}
        />
      )}

      {!isCalendar && (
        <Table
          dataSource={filteredSchedules?.filter((schedule) => {
            const filterDate = schedule[filterKey[paginateBy]];

            if (typeof filterDate === 'string') {
              return moment(filterDate, 'YYYY-MM-DD').isSame(
                moment(date),
                'day',
              );
            }

            return true;
          })}
          scroll={{ x: true }}
          pagination={false}
          columns={[
            {
              dataIndex: 'status',
              title: 'Status',
              render: (_, record) => (
                <WorkOrderStatusTag
                  color={record?.status?.color || 'gray'}
                  label={record?.status?.name || 'Pendente'}
                />
              ),
              ellipsis: true,
            },
            { dataIndex: 'customer', title: 'Cliente', ellipsis: true },
            {
              dataIndex: 'licensePlate',
              title: 'Veículo',
              render: (_, record) =>
                !!record.licensePlate && !!record.brand
                  ? `${record.licensePlate} • ${record.brand}`
                  : '-',
              ellipsis: true,
            },
            {
              dataIndex: filterKey[paginateBy],
              title: 'Data',
              render: (row) => moment(row).format('DD/MM/YYYY'),
              ellipsis: true,
            },
            {
              title: 'Ação',
              fixed: 'right',
              align: 'center',
              width: 40,
              render: (_, record) => (
                <EditIcon
                  iconProps={{
                    onClick: () => handleSelectSchedule(record.activityId),
                  }}
                />
              ),
            },
          ]}
        />
      )}

      <Alert
        style={{ marginTop: 24 }}
        description={
          <>
            <p>
              <strong>A Tela de Agendamentos tiveram mudanças.</strong>
            </p>
            <div>
              A aba "<strong>Agendamentos</strong>" foi desativada e suas
              funcionalidades foram movidas para a aba "
              <strong>Acompanhamento</strong>" em formato de listagem.
            </div>
            <div>
              Novos filtros e modos de visualização foram adicionados para
              facilitar a gestão das atividades.
            </div>
          </>
        }
      />

      <ScheduleEditModal
        activityId={activityId}
        title="Visualização/Edição do Agendamento"
        open={modalEditOpened}
        onCancel={modalEditAction.close}
      />

      <ScheduleCreateModal
        companyIds={companyIds}
        open={scheduleAddOpened}
        onCancel={scheduleAddAction.close}
      />
    </DefaultRouteTabs>
  );
}
